import React from 'react';
import { CircleMarker, Marker, Polyline, Tooltip } from 'react-leaflet';
import theme from '../../../theme';
import { makeTrailStartIcon, makeGapIcon } from './mapIcons';
import { pathMidpoint } from './mapGeometry';
import {
  groupTrailByDriver,
  splitTrailAtGaps,
  nearestTrailPoint,
  simplifyLatLngs,
  strideForBudget,
  formatGapDuration,
} from './trailGeometry';
import { MIN_LINE_POINTS } from './routePaths';
import { formatClockTime } from '../../../utils/displayFormat';

// Everything a driver's recorded GPS trail contributes to a map, shared by
// the Single-route view (one trail, with stops and predictions around it)
// and the Global view (every visible route's trail at once): a solid line per
// continuously reported run (GPS jitter simplified out of the drawn line —
// the hover card still snaps to the exact readings), a faint dotted connector
// plus a diamond over each silence the phone never filled in, thinned dots
// where the readings landed, and a ring at the first reading. Plain
// functions, no hooks.

// The three line weights the route maps draw with.
export const LINE = {
  solid: { weight: 5, opacity: 0.95, dashArray: undefined },
  dashed: { weight: 3.5, opacity: 0.85, dashArray: '2 8' },
  // Inferred (backward) predicted chain — fainter because its order is a
  // guess, not driven.
  faint: { weight: 2.5, opacity: 0.55, dashArray: '2 8' },
};

const GAP_DASH = '1 9';
// Most trail dots to hand Leaflet for ONE route. A full day of pings runs to
// thousands of markers, which stalls panning long before it adds any detail.
export const TRAIL_DOT_BUDGET = 2000;
// The Global view splits one budget across every visible route, so a busy
// region stays pannable no matter how many trails are drawn at once.
export const GLOBAL_TRAIL_DOT_BUDGET = 4000;

const trailLatLng = (point) => [point.latitude, point.longitude];

// `options` overrides the style's color / dash and attaches an optional
// tooltip and hover handlers — react-leaflet 2 turns `onMouseMove` /
// `onMouseOut` into the Leaflet `mousemove` / `mouseout` events.
export function line(key, positions, style, options = {}) {
  const {
    color = theme.colors.routeLine,
    dashArray = style.dashArray,
    tooltip = null,
    onMouseMove = null,
    onMouseOut = null,
    onMouseOver = null,
  } = options;
  return (
    <Polyline
      key={key}
      positions={positions}
      color={color}
      weight={style.weight}
      opacity={style.opacity}
      dashArray={dashArray}
      onMouseMove={onMouseMove}
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
    >
      {tooltip}
    </Polyline>
  );
}

// Every interactive line that is NOT recorded trail dismisses the hover card
// on entry: a gap connector or a helper's hop means the cursor has left
// recorded ground, and the card must not linger over a stretch with no
// reading behind it. Null when nobody is listening for hovers.
export const hoverCardDismisser = (onTrailHover) =>
  onTrailHover ? () => onTrailHover(null) : null;

// Total recorded positions across a set of drivers — the Global view divides
// its dot budget by this.
export function totalTrailPoints(drivers) {
  return drivers.reduce(
    (total, driver) => total + (driver.pingTrail || []).length,
    0,
  );
}

// Marks the first recorded point of a driver's driven trail — where the map
// starts telling the "as driven" story, distinct from every stop marker.
function trailStartMarker(driver, point) {
  return (
    <Marker
      key={`start-${driver.routeId}`}
      position={trailLatLng(point)}
      icon={makeTrailStartIcon()}
    >
      <Tooltip>{`Start · ${formatClockTime(new Date(point.timestampMs), {
        zone: true,
      })}`}</Tooltip>
    </Marker>
  );
}

/**
 * Build the trail layers for one driver's recorded positions.
 *
 * @param {object} driver The route's driver record (keys + hover identity).
 * @param {Array<object>} pingTrail Recorded positions, oldest first.
 * @param {object} [options]
 * @param {Function} [options.onTrailHover] Receives the recorded point
 *   nearest the cursor while it is on the trail, then null once it leaves.
 * @param {number} [options.dotStride] Draw every Nth reading as a dot. The
 *   Global view passes a stride computed from its shared budget; by default
 *   the stride comes from `TRAIL_DOT_BUDGET` for this trail alone.
 * @returns {{trailPolylines: Array, gapPolylines: Array, gapMarkers: Array,
 *            trailDots: Array, startMarker: object|null}}
 */
export function buildTrailLayers(
  driver,
  pingTrail,
  { onTrailHover = null, dotStride = null } = {},
) {
  const trailPolylines = [];
  const gapPolylines = [];
  const gapMarkers = [];
  let gapIndex = 0;
  const clearHoverCard = hoverCardDismisser(onTrailHover);

  groupTrailByDriver(pingTrail).forEach(([groupKey, groupPoints]) => {
    const { runs, gaps } = splitTrailAtGaps(groupPoints);
    runs.forEach((run, runIndex) => {
      if (run.length < MIN_LINE_POINTS) {
        return;
      }
      const hoverHandlers = onTrailHover
        ? {
            onMouseMove: (event) =>
              onTrailHover(nearestTrailPoint(run, event.latlng)),
            onMouseOut: () => onTrailHover(null),
          }
        : {};
      trailPolylines.push(
        line(
          `pl-trail-${driver.routeId}-${groupKey}-${runIndex}`,
          simplifyLatLngs(run.map(trailLatLng)),
          LINE.solid,
          hoverHandlers,
        ),
      );
    });
    gaps.forEach((gap) => {
      const label = `GPS data missing · no reading for ${formatGapDuration(
        gap.minutes,
      )}`;
      const positions = [trailLatLng(gap.from), trailLatLng(gap.to)];
      gapPolylines.push(
        line(`pl-gap-${driver.routeId}-${gapIndex}`, positions, LINE.faint, {
          dashArray: GAP_DASH,
          tooltip: <Tooltip sticky>{label}</Tooltip>,
          onMouseOver: clearHoverCard,
        }),
      );
      gapMarkers.push(
        <Marker
          key={`gap-${driver.routeId}-${gapIndex}`}
          position={pathMidpoint(positions).point}
          icon={makeGapIcon()}
        >
          <Tooltip>{label}</Tooltip>
        </Marker>,
      );
      gapIndex += 1;
    });
  });

  const trailDots = [];
  const stride =
    dotStride || strideForBudget(pingTrail.length, TRAIL_DOT_BUDGET);
  for (let index = 0; index < pingTrail.length; index += stride) {
    trailDots.push(
      <CircleMarker
        key={`dot-${driver.routeId}-${index}`}
        center={trailLatLng(pingTrail[index])}
        radius={2.5}
        color={theme.colors.routeLine}
        fillOpacity={0.9}
        stroke={false}
        interactive={false}
      />,
    );
  }

  const startMarker =
    pingTrail.length >= MIN_LINE_POINTS
      ? trailStartMarker(driver, pingTrail[0])
      : null;

  return { trailPolylines, gapPolylines, gapMarkers, trailDots, startMarker };
}
